/**
 * React Query Keys
 *
 * Centralized keys for all React Query operations
 * Organized by service and operation type
 */

// ==================== AUTH SERVICE KEYS ====================
export const AUTH_KEYS = {
  LOGIN: 'auth:login',
  REFRESH_TOKEN: 'auth:refresh-token',
  USER_ME: 'auth:user-me',
  USER_INFO: 'auth:user-info',
  SESSIONS: 'auth:sessions',
  LOGOUT: 'auth:logout',
  LOGOUT_ALL: 'auth:logout-all',
  CURRENT_USER: 'auth:current-user',
  SELECT_ESTABLISHMENT: 'auth:select-establishment',
  SELECT_PROFILE: 'auth:select-profile',
} as const;

// ==================== ESTABLISHMENT SERVICE KEYS ====================
export const ESTABLISHMENT_KEYS = {
  // Withdraws management
  GET_ESTABLISHMENT_WITHDRAWS_HISTORY: 'establishment:withdraws:history:get',

  // Cash flow management
  GET_ESTABLISHMENT_CASH_FLOW_SUMMARY: 'establishment:cash-flow:summary:get',
  GET_ESTABLISHMENT_CASH_FLOW_MOVEMENTS: 'establishment:cash-flow:movements:get',
  // Lunchbox management
  GET_ESTABLISHMENT_LUNCHBOX_DASHBOARD: 'establishment:lunchbox:dashboard:get',
  GET_ESTABLISHMENT_LUNCHBOX_CLASS_DETAIL: 'establishment:lunchbox:class-detail:get',

  // Sales management
  GET_ESTABLISHMENT_SALES: 'establishment:sales:get',

  // Balance management
  GET_ESTABLISHMENT_BALANCE: 'establishment:balance:get',

  // Dashboard management
  GET_DASHBOARD: 'establishment:dashboard:get',

  // Establishment management
  CREATE_ESTABLISHMENT: 'establishment:create',
  UPDATE_ESTABLISHMENT: 'establishment:update',
  GET_ESTABLISHMENT: 'establishment:get',
  LIST_ESTABLISHMENTS: 'establishment:list',
  LIST_ESTABLISHMENT_TYPES: 'establishment:types',
  LIST_BANKS: 'establishment:banks',
  GET_ESTABLISHMENTS_BY_INSTITUTION: 'establishment:by-institution',
  ESTABLISHMENT_EXISTS: 'establishment:exists',
  GET_ALL_ESTABLISHMENTS: 'establishment:all',

  // Paytime (Conta Digital)
  GET_PAYTIME_INFO: 'establishment:paytime:info',

  // User management
  LIST_ESTABLISHMENT_USERS: 'establishment:users:list',
  LIST_ALL_ESTABLISHMENT_USERS: 'establishment:users:list-all',
  LIST_ALL_ESTABLISHMENT_OPERATORS: 'establishment:users:list-all-operators',
  EDIT_ESTABLISHMENT_USER: 'establishment:users:edit',
  DELETE_ESTABLISHMENT_USER: 'establishment:users:delete',
  CREATE_ESTABLISHMENT_INTERNAL_USER: 'establishment:users:create-internal',
  CHECK_INTERNAL_USER_BY_CPF: 'establishment:users:check-internal-by-cpf',
  ADD_INTERNAL_MEMBERSHIP: 'establishment:users:add-internal-membership',

  // Interval management
  CREATE_INTERVAL: 'establishment:intervals:create',
  UPDATE_INTERVAL: 'establishment:intervals:update',
  LIST_INTERVALS: 'establishment:intervals:list',

  // Class management
  UPDATE_CLASS: 'establishment:classes:update',
  LIST_CLASSES: 'establishment:classes:list',

  // Menu management
  CREATE_MENU: 'establishment:menus:create',
  UPLOAD_MENU_PHOTO: 'establishment:menus:upload-photo',
  LIST_MENUS: 'establishment:menus:list',
  UPDATE_MENU: 'establishment:menus:update',
  GET_MENUS_WITH_PRODUCTS: 'establishment:menus:with-products',
  GET_PRODUCTS_BY_MENU: 'establishment:menus:products-by-menu',
  GET_PRODUCTS_WITH_CLASS_PRICING: 'establishment:menus:products-with-pricing',
  ATTACH_MENU: 'establishment:menus:attach',
  DETACH_MENU: 'establishment:menus:detach',
  BACKOFFICE_ATTACH_MENU: 'establishment:menus:backoffice-attach',
  BACKOFFICE_DETACH_MENU: 'establishment:menus:backoffice-detach',

  // Product management
  CREATE_PRODUCT: 'establishment:products:create',
  LIST_PRODUCTS: 'establishment:products:list',
  GET_PRODUCT_DETAIL: 'establishment:products:detail',
  UPDATE_PRODUCT: 'establishment:products:update',
  UPDATE_PRODUCT_STATUS: 'establishment:products:update-status',
  GET_PRODUCT_SUGGESTIONS: 'establishment:products:suggestions',
  GET_GLOBAL_PRODUCT_LIST: 'establishment:products:global-list',
  LINK_PRODUCTS_BATCH: 'establishment:products:link-batch',
  LIST_PRODUCTS_ALL: 'establishment:products:list-all',

  // Stock management
  ADD_STOCK: 'establishment:stock:add',
  SCHEDULE_STOCK_TRANSFER: 'establishment:stock:schedule-transfer',
  LIST_STOCK_TRANSFERS: 'establishment:stock:transfers',
  EXECUTE_STOCK_TRANSFER: 'establishment:stock:execute-transfer',
  CANCEL_STOCK_TRANSFER: 'establishment:stock:cancel-transfer',
  LIST_STOCK: 'establishment:stock:list',
  LIST_STOCK_SUMMARY: 'establishment:stock:summary',
  LIST_STOCK_MOVEMENTS: 'establishment:stock:movements',
  CREATE_STOCK_ENTRY: 'establishment:stock:create-entry',
  CREATE_STOCK_OUT: 'establishment:stock:create-out',
  LIST_STOCK_NFS: 'establishment:stock:nfs',
  LIST_STOCK_EXPIRATION: 'establishment:stock:expiration',
  STOCK_TRANSFER: 'establishment:stock:transfer',

  // Purchase payment
  PAY_PURCHASE: 'establishment:purchase:pay',
  PURCHASE_PIX_INIT: 'establishment:purchase:pix:init',
  PURCHASE_PIX_CONFIRM: 'establishment:purchase:pix:confirm',
  PURCHASE_PAYMENT_RECEIPT: 'establishment:purchase:payment:receipt',

  // Extract management
  GET_ESTABLISHMENT_EXTRACT: 'establishment:extract:get',
  // Totem billing
  GET_ESTABLISHMENT_TOTEM_BILLING_SUMMARY: 'establishment:totem-billing:summary:get',

  // Orders Reports
  GET_ORDERS_REPORT_OVERALL: 'orders:report:overall',
  GET_ORDERS_REPORT_OVERALL_BY_INTERVAL: 'orders:report:overall-by-interval',
  GET_ORDERS_REPORT_BY_ATTENDANT: 'orders:report:by-attendant',
  GET_ORDERS_REPORT_PCT_UNDER_TARGET: 'orders:report:pct-under-target',

  // Supplier management
  LIST_SUPPLIERS: 'establishment:supplier:list',
  GET_SUPPLIER: 'establishment:supplier:get',
  CREATE_SUPPLIER: 'establishment:supplier:create',
  UPDATE_SUPPLIER: 'establishment:supplier:update',
  DELETE_SUPPLIER: 'establishment:supplier:delete',

  // Tax Integration
  GET_TAX_INTEGRATION_STATUS: 'establishment:tax:integration:status',
  GET_TAX_CONFIG: 'establishment:tax:config',
  GET_TAX_CST_LIST: 'establishment:tax:cst:list',
  ENABLE_TAX_INTEGRATION: 'establishment:tax:integration:enable',
  CONFIGURE_TAX_INTEGRATION: 'establishment:tax:integration:configure',
  DELETE_TAX_INTEGRATION: 'establishment:tax:integration:delete',
  DELETE_TAX_CERTIFICATE: 'establishment:tax:certificate:delete',
  UPLOAD_TAX_CERTIFICATE: 'establishment:tax:certificate:upload',
} as const;

// ==================== INSTITUTION SERVICE KEYS ====================
export const INSTITUTION_KEYS = {
  GET_INSTITUTIONS: 'institution:get-all',
  CREATE_INSTITUTION: 'institution:create',
  UPDATE_INSTITUTION: 'institution:update',
  GET_INSTITUTION: 'institution:get',
  LIST_INSTITUTIONS: 'institution:list',
  DELETE_INSTITUTION: 'institution:delete',
  GET_INSTITUTION_ESTABLISHMENTS: 'institution:establishments',
  DROPDOWN_INSTITUTIONS: 'institution:dropdown',
  FIND_INSTITUTIONS: 'institution:find',
} as const;

// ==================== LUNCHBOX SERVICE KEYS ====================
export const LUNCHBOX_KEYS = {
  CREATE_LUNCHBOX: 'lunchbox:create',
  UPDATE_LUNCHBOX: 'lunchbox:update',
  GET_LUNCHBOX: 'lunchbox:get',
  LIST_LUNCHBOXES: 'lunchbox:list',
  DELETE_LUNCHBOX: 'lunchbox:delete',
  GET_LUNCHBOX_BALANCE: 'lunchbox:balance',
  GET_LUNCHBOX_HISTORY: 'lunchbox:history',
  TRANSFER_LUNCHBOX: 'lunchbox:transfer',
} as const;

// ==================== ORDER SERVICE KEYS ====================
export const ORDER_KEYS = {
  // Queries principais
  GET_ORDER: 'order:get',
  LIST_ORDERS: 'order:list',
  GET_ORDER_STATISTICS: 'order:statistics',

  // Mutations
  UPDATE_ORDER: 'order:update',
  PROCESS_ORDER: 'order:process',
  CANCEL_ORDER: 'order:cancel',
} as const;

// ==================== PRODUCT SERVICE KEYS ====================
export const PRODUCT_KEYS = {
  // Queries principais
  GET_PRODUCT: 'product:get',
  LIST_PRODUCTS: 'product:list',
  GET_PRODUCT_CATEGORIES: 'product:categories',
  GET_PRODUCT_SUGGESTIONS: 'product:suggestions',
  SEARCH_PRODUCTS: 'product:search',

  // Mutations
  CREATE_PRODUCT: 'product:create',
  UPDATE_PRODUCT: 'product:update',
  DELETE_PRODUCT: 'product:delete',
} as const;

// ==================== STUDENT SERVICE KEYS ====================
export const STUDENT_KEYS = {
  // Queries principais
  GET_STUDENT: 'student:get',
  LIST_STUDENTS: 'student:list',
  SEARCH_STUDENTS: 'student:search',
  GET_STUDENT_BY_CPF: 'student:by-cpf',
  GET_STUDENT_BY_EMAIL: 'student:by-email',
  GET_STUDENT_HISTORY: 'student:history',
  GET_STUDENT_BALANCE: 'student:balance',

  // Novos endpoints específicos
  GET_STUDENT_LIMITS: 'student:limits',
  GET_STUDENT_RESTRICTIONS: 'student:restrictions',
  GET_STUDENT_CHILDREN: 'student:children',
  GET_STUDENT_CLASSES: 'student:classes',

  // Mutations
  CREATE_STUDENT: 'student:create',
  UPDATE_STUDENT: 'student:update',
  DELETE_STUDENT: 'student:delete',
  TRANSFER_STUDENT: 'student:transfer',
} as const;

// ==================== TOTEM SERVICE KEYS ====================
export const TOTEM_KEYS = {
  // CRUD operations
  CREATE_TOTEM: 'totem:create',
  UPDATE_TOTEM: 'totem:update',
  DELETE_TOTEM: 'totem:delete',
  GET_TOTEM: 'totem:get',
  LIST_TOTEMS: 'totem:list',
  TOTEM_EXISTS: 'totem:exists',

  // Statistics and monitoring
  GET_TOTEM_STATS: 'totem:stats',
  UPDATE_TOTEM_STATUS: 'totem:status:update',
  SEND_KEEP_ALIVE: 'totem:keepalive',

  // Search and filtering
  SEARCH_TOTEMS: 'totem:search',
  GET_TOTEMS_BY_ESTABLISHMENT: 'totem:by-establishment',
  GET_TOTEMS_BY_STATUS: 'totem:by-status',
} as const;

// ==================== TRANSACTION SERVICE KEYS ====================
export const TRANSACTION_KEYS = {
  // Queries principais
  GET_TRANSACTION: 'transaction:get',
  LIST_TRANSACTIONS: 'transaction:list',
  GET_TRANSACTION_HISTORY: 'transaction:history',
  GET_TRANSACTION_STATISTICS: 'transaction:statistics',
  GET_TRANSACTION_REPORTS: 'transaction:reports',

  // Mutations
  CREATE_TRANSACTION: 'transaction:create',
  UPDATE_TRANSACTION: 'transaction:update',
  DELETE_TRANSACTION: 'transaction:delete',
  PROCESS_TRANSACTION: 'transaction:process',
  CANCEL_TRANSACTION: 'transaction:cancel',
} as const;

// ==================== USER SERVICE KEYS ====================
export const USER_KEYS = {
  CREATE_USER: 'user:create',
  UPDATE_USER: 'user:update',
  GET_USER: 'user:get',
  LIST_USERS: 'user:list',
  DELETE_USER: 'user:delete',
  GET_USER_PROFILE: 'user:profile',
  UPDATE_USER_PROFILE: 'user:profile:update',
  GET_USER_PERMISSIONS: 'user:permissions',
  GET_USER_ACTIVITY: 'user:activity',
} as const;

// ==================== BACKOFFICE SERVICE KEYS ====================
export const BACKOFFICE_KEYS = {
  // Menu operations
  LIST_MENUS: 'backoffice:menus:list',
  GET_MENU: 'backoffice:menus:get',

  // Establishment operations
  LIST_ESTABLISHMENTS: 'backoffice:establishments:list',
  GET_ESTABLISHMENT: 'backoffice:establishments:get',
  GET_ESTABLISHMENT_STATEMENT: 'backoffice:establishments:statement',
  LIST_ESTABLISHMENT_DEVICES: 'backoffice:establishments:devices:list',
  BLOCK_DEVICE: 'backoffice:devices:block',
  REGENERATE_DEVICE_SECRET: 'backoffice:devices:regenerate-secret',
  CREATE_ESTABLISHMENT_DEVICE: 'backoffice:establishments:devices:create',
  GET_TOTEM_CARDS: 'backoffice:totem:cards:get',
  LIST_TOTEMS: 'backoffice:totem:list',

  // Establishment product operations
  LIST_ESTABLISHMENT_PRODUCTS: 'backoffice:establishments:products:list',
  GET_PRODUCT_CONFIGURATION: 'backoffice:products:configuration:get',
  UPDATE_PRODUCT_CONFIGURATION: 'backoffice:products:configuration:update',

  // Backoffice product search
  SEARCH_PRODUCTS: 'backoffice:products:search',

  // Backoffice institution operations
  SEARCH_INSTITUTIONS: 'backoffice:institutions:search',
  GET_INSTITUTION_DETAIL: 'backoffice:institutions:detail',

  // Student operations
  GET_STUDENTS_OVERVIEW: 'backoffice:students:overview',
  SEARCH_STUDENTS: 'backoffice:students:search',
  GET_STUDENT_GENERAL: 'backoffice:students:general',
  GET_STUDENT_STATEMENT: 'backoffice:students:statement',
  GET_STUDENT_TRANSACTION_DETAILS: 'backoffice:students:transaction-details',
  GET_STUDENT_RESTRICTIONS: 'backoffice:students:restrictions',

  // User operations
  GET_USERS_OVERVIEW: 'backoffice:users:overview',
  SEARCH_USERS: 'backoffice:users:search',
  GET_USER: 'backoffice:users:get',
  GET_USER_GENERAL: 'backoffice:users:get-general',

  // Global operations
  GLOBAL_SEARCH: 'backoffice:search:global',
  STUDENT_COMPLETE_PROFILE: 'backoffice:students:complete-profile',
  GET_TRANSACTION: 'backoffice:transactions:get',

  // Dashboard
  GET_DASHBOARD: 'backoffice:dashboard:get',
} as const;

// ==================== VIACEP SERVICE KEYS ====================
export const VIACEP_KEYS = {
  GET_ADDRESS_BY_CEP: 'viacep:address',
  VALIDATE_CEP: 'viacep:validate',
} as const;

// ==================== COMBINED KEYS ====================
export const QUERY_KEYS = {
  AUTH: AUTH_KEYS,
  ESTABLISHMENT: ESTABLISHMENT_KEYS,
  INSTITUTION: INSTITUTION_KEYS,
  LUNCHBOX: LUNCHBOX_KEYS,
  ORDER: ORDER_KEYS,
  PRODUCT: PRODUCT_KEYS,
  STUDENT: STUDENT_KEYS,
  TOTEM: TOTEM_KEYS,
  TRANSACTION: TRANSACTION_KEYS,
  USER: USER_KEYS,
  BACKOFFICE: BACKOFFICE_KEYS,
  VIACEP: VIACEP_KEYS,
} as const;
